import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import EditIcon from "@mui/icons-material/Edit";
import TopNavBar from "../components/TopNavBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDevices } from "../api/devices";
import { getCustomers } from "../api/customer";
import { getDeviceServicePlans } from "../api/devicePlans";

function normalizeDevices(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.devices)) return payload.devices;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeCustomers(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.customers)) return payload.customers;
  return [];
}

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

export default function ConfigureDevicePlansPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [plansActiveToday, setPlansActiveToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [customerFilter, setCustomerFilter] = useState(
    () => searchParams.get("customer") || "all",
  );
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || "all",
  );

  // Keep the URL in sync so search/filters survive navigating away and back.
  useEffect(() => {
    const nextParams = {};
    if (search) nextParams.q = search;
    if (customerFilter !== "all") nextParams.customer = customerFilter;
    if (statusFilter !== "all") nextParams.status = statusFilter;
    setSearchParams(nextParams, { replace: true });
  }, [search, customerFilter, statusFilter, setSearchParams]);

  const clearFilters = () => {
    setSearch("");
    setCustomerFilter("all");
    setStatusFilter("all");
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    const today = todayDateString();

    Promise.all([
      getDevices(),
      getCustomers(),
      getDeviceServicePlans(),
      getDeviceServicePlans({ activeOnDate: today, active: true }),
    ])
      .then(([deviceData, customerData, plansData, plansTodayData]) => {
        setDevices(normalizeDevices(deviceData));
        setCustomers(normalizeCustomers(customerData));
        setPlans(Array.isArray(plansData?.plans) ? plansData.plans : []);
        setPlansActiveToday(
          Array.isArray(plansTodayData?.plans) ? plansTodayData.plans : [],
        );
      })
      .catch((err) => setError(err.message || "Failed to load device plans"))
      .finally(() => setLoading(false));
  }, []);

  // A device can have one active plan PER service type (e.g. on-site + remote),
  // so each device maps to an array of plans instead of a single one.
  const plansByDeviceId = useMemo(() => {
    const map = new Map();
    plans.forEach((plan) => {
      const key = String(plan.device_id || "");
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(plan);
    });

    map.forEach((devicePlans) => {
      devicePlans.sort((a, b) =>
        String(a.service_type_name || "").localeCompare(
          String(b.service_type_name || ""),
        ),
      );
    });

    return map;
  }, [plans]);

  const customerNameById = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => {
      map.set(
        String(customer.pk_id),
        customer.uk_name || customer.customer_name || "Unknown",
      );
    });
    return map;
  }, [customers]);

  const activeTodayDeviceIds = useMemo(() => {
    const ids = new Set();
    plansActiveToday.forEach((plan) => {
      if (Number(plan.active) === 1) {
        ids.add(String(plan.device_id || ""));
      }
    });
    return ids;
  }, [plansActiveToday]);

  const filteredDevices = useMemo(() => {
    const query = String(search || "")
      .trim()
      .toLowerCase();

    return devices.filter((device) => {
      const devicePkId = String(device.pk_id || "");
      const rcDeviceId = String(device.rc_device_id || "");
      const deviceName = String(device.name || device.device_name || "");
      const customerId = String(
        device.customer_id || device.customer_pk_id || "",
      );
      const customerName = String(
        device.customer_name || customerNameById.get(customerId) || "",
      );
      const devicePlans = plansByDeviceId.get(devicePkId) || [];
      const isActiveToday = activeTodayDeviceIds.has(devicePkId);

      if (customerFilter !== "all") {
        if (customerId !== String(customerFilter)) return false;
      }

      if (statusFilter === "assigned" && devicePlans.length === 0) return false;
      if (statusFilter === "unassigned" && devicePlans.length > 0) return false;
      if (
        statusFilter === "enabled" &&
        !devicePlans.some((plan) => Number(plan.active) === 1)
      )
        return false;
      if (
        statusFilter === "disabled" &&
        !devicePlans.some((plan) => Number(plan.active) === 0)
      )
        return false;
      if (statusFilter === "active_today" && !isActiveToday) return false;
      if (statusFilter === "not_active_today" && isActiveToday) return false;

      if (!query) return true;

      return [deviceName, rcDeviceId, customerName]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query));
    });
  }, [
    devices,
    search,
    customerFilter,
    statusFilter,
    customerNameById,
    plansByDeviceId,
    activeTodayDeviceIds,
  ]);

  return (
    <>
      <TopNavBar heading="Configure Device Plans" />
      <Container maxWidth="lg" sx={{ mt: 3, pb: 4 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
          <SettingsSuggestIcon sx={{ color: "text.secondary" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Assign Automatic Device Plans
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              label="Search robot"
              placeholder="Search by robot name, rc_device_id, or customer"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={() => setSearch("")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />

            <TextField
              select
              label="Customer"
              sx={{ minWidth: { xs: "100%", md: 260 } }}
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <MenuItem value="all">All customers</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.pk_id} value={String(customer.pk_id)}>
                  {customer.uk_name ||
                    customer.customer_name ||
                    `Customer #${customer.pk_id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Assignment"
              sx={{ minWidth: { xs: "100%", md: 190 } }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="active_today">Active today</MenuItem>
              <MenuItem value="not_active_today">Not active today</MenuItem>
            </TextField>

            <Tooltip title="Clear search and filters">
              <IconButton
                onClick={clearFilters}
                sx={{ alignSelf: { md: "center" } }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {!loading && !error && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, textAlign: "right" }}
          >
            {filteredDevices.length} of {devices.length} device
            {devices.length === 1 ? "" : "s"} shown
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && filteredDevices.length === 0 && (
          <Typography color="text.secondary">
            No matching devices found.
          </Typography>
        )}

        {!loading && !error && filteredDevices.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Robot</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Service Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Route Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Active Today
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Overrides
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDevices.map((device) => {
                  const devicePkId = String(device.pk_id || "");
                  const devicePlans = plansByDeviceId.get(devicePkId) || [];
                  const customerId = String(
                    device.customer_id || device.customer_pk_id || "",
                  );
                  const customerName =
                    device.customer_name ||
                    customerNameById.get(customerId) ||
                    "Unassigned";
                  const isActiveToday = activeTodayDeviceIds.has(devicePkId);

                  return (
                    <TableRow
                      key={device.pk_id || device.rc_device_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/configure-device-plans/${device.pk_id}`)
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {device.name ||
                            device.device_name ||
                            `Device #${device.pk_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {device.rc_device_id || "No rc_device_id"}
                        </Typography>
                      </TableCell>
                      <TableCell>{customerName}</TableCell>
                      <TableCell>
                        {devicePlans.length === 0 ? (
                          "Not assigned"
                        ) : (
                          <Stack spacing={0.5}>
                            {devicePlans.map((plan) => (
                              <Box
                                key={plan.pk_id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {plan.service_type_name || "Service plan"}:
                                </Typography>
                                <Typography variant="body2">
                                  {plan.service_plan_template_name ||
                                    "Not assigned"}
                                </Typography>
                                <Chip
                                  label={
                                    Number(plan.active) === 1
                                      ? "Enabled"
                                      : "Disabled"
                                  }
                                  size="small"
                                  color={
                                    Number(plan.active) === 1
                                      ? "success"
                                      : "warning"
                                  }
                                  variant="outlined"
                                />
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        {devicePlans.length === 0 ? (
                          "Not assigned"
                        ) : (
                          <Stack spacing={0.5}>
                            {devicePlans.map((plan) => (
                              <Typography key={plan.pk_id} variant="body2">
                                {plan.route_template_name || "Not assigned"}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isActiveToday ? "Yes" : "No"}
                          size="small"
                          color={isActiveToday ? "success" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {devicePlans.reduce(
                          (sum, plan) => sum + (plan.overrides_count || 0),
                          0,
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            devicePlans.length > 0
                              ? "Edit assignment"
                              : "Create assignment"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/configure-device-plans/${device.pk_id}`,
                              );
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>
    </>
  );
}
